import type { MarketingNiche, MarketingPreset } from "@/data/marketing/content";
import solutionsEn from "@/data/marketing/solutions.en.json";

type SiteLocale = "ru" | "en";

type PresetEn = Omit<MarketingPreset, "presetId"> & { slug: string };
type NicheEn = Omit<MarketingNiche, "presetId" | "url"> & { nicheId: string };

const presetsBySlug = new Map(
    ((solutionsEn as { presets: PresetEn[] }).presets || []).map((p) => [p.slug, p]),
);
const nichesById = new Map(
    ((solutionsEn as { niches: NicheEn[] }).niches || []).map((n) => [n.nicheId, n]),
);

export function getSiteUiLocale(language?: string): SiteLocale {
    return language?.startsWith("en") ? "en" : "ru";
}

/** Merge EN marketing copy over RU preset fields when locale is English. */
export function resolvePresetCopy(preset: MarketingPreset, locale: SiteLocale): MarketingPreset {
    if (locale !== "en") return preset;
    const en = presetsBySlug.get(preset.slug);
    if (!en) return preset;
    return {
        ...preset,
        label: en.label || preset.label,
        seoTitle: en.seoTitle || preset.seoTitle,
        seoDescription: en.seoDescription || preset.seoDescription,
        h1: en.h1 || preset.h1,
        subhead: en.subhead || preset.subhead,
        audience: en.audience || preset.audience,
        clientJob: en.clientJob || preset.clientJob,
        ownerJob: en.ownerJob || preset.ownerJob,
        painPoints: en.painPoints?.length ? en.painPoints : preset.painPoints,
        scenarioSteps: en.scenarioSteps?.length ? en.scenarioSteps : preset.scenarioSteps,
        modules: en.modules?.length ? en.modules : preset.modules,
        nicheTeasers: en.nicheTeasers?.length
            ? en.nicheTeasers.map((t, i) => ({
                  nicheId: t.nicheId || preset.nicheTeasers[i]?.nicheId || "",
                  label: t.label || preset.nicheTeasers[i]?.label || "",
                  teaser: t.teaser || preset.nicheTeasers[i]?.teaser || "",
              }))
            : preset.nicheTeasers,
        faq: en.faq?.length ? en.faq : preset.faq,
        primaryCta: en.primaryCta || preset.primaryCta,
    };
}

/** Merge EN marketing copy over RU niche fields when locale is English. */
export function resolveNicheCopy(niche: MarketingNiche, locale: SiteLocale): MarketingNiche {
    if (locale !== "en") return niche;
    const en = nichesById.get(niche.nicheId);
    if (!en) return niche;
    return {
        ...niche,
        label: en.label || niche.label,
        seoTitle: en.seoTitle || niche.seoTitle,
        seoDescription: en.seoDescription || niche.seoDescription,
        h1: en.h1 || niche.h1,
        accentLine: en.accentLine ?? niche.accentLine,
        subhead: en.subhead || niche.subhead,
        forWhom: en.forWhom || niche.forWhom,
        pains: en.pains?.length ? en.pains : niche.pains,
        clientJourney: en.clientJourney?.length ? en.clientJourney : niche.clientJourney,
        ownerWins: en.ownerWins?.length ? en.ownerWins : niche.ownerWins,
        modulesEmphasized: en.modulesEmphasized?.length
            ? en.modulesEmphasized
            : niche.modulesEmphasized,
        differentiatorsVsPreset: en.differentiatorsVsPreset || niche.differentiatorsVsPreset,
        faq: en.faq?.length ? en.faq : niche.faq,
        primaryCta: en.primaryCta || niche.primaryCta,
    };
}
